import { defineStore } from 'pinia'
import type { AxiosRequestConfig } from 'axios'
import type { MyZoneRequestConfig } from '@/api/serviceProviders/my-zone'

export const useInterruptedRequestStore = defineStore('interruptedRequest', {
    state: () => ({
        requestConfig: null as MyZoneRequestConfig | null,
        pendingResolvers: null as null | {
            resolve: (value: unknown) => void
            reject: (reason?: unknown) => void
        },
    }),
    actions: {
        setRequest(config: AxiosRequestConfig | null) {
            if (!config?.url) {
                this.requestConfig = null

                return
            }

            const normalizedHeaders = config.headers
                ? JSON.parse(JSON.stringify(config.headers))
                : undefined
            const extendedConfig = config as MyZoneRequestConfig

            this.requestConfig = {
                url: config.url,
                method: config.method,
                data: config.data,
                params: config.params,
                headers: normalizedHeaders,
                baseURL: config.baseURL,
                withCredentials: config.withCredentials,
                responseType: config.responseType,
                skipAuthRefresh: extendedConfig.skipAuthRefresh,
                skipRefreshRetry: extendedConfig.skipRefreshRetry,
                withAppLoader: extendedConfig.withAppLoader,
                // callers rely on these (e.g. CloudTalk 404 handling): a retried request must keep them
                validateStatus: config.validateStatus,
                transformResponse: config.transformResponse,
            }
        },
        captureRequest(config: AxiosRequestConfig | null) {
            if (!config?.url) {
                return Promise.reject(new Error('Request configuration missing'))
            }

            this.setRequest(config)

            if (this.pendingResolvers) {
                this.pendingResolvers.reject(new Error('Pending request superseded'))
                this.pendingResolvers = null
            }

            return new Promise((resolve, reject) => {
                this.pendingResolvers = {
                    resolve,
                    reject,
                }
            })
        },
        clearRequest() {
            this.requestConfig = null
            if (this.pendingResolvers) {
                this.pendingResolvers.reject(new Error('Request cleared'))
                this.pendingResolvers = null
            }
        },
        resolvePending(value?: unknown) {
            this.requestConfig = null
            if (this.pendingResolvers) {
                this.pendingResolvers.resolve(value)
                this.pendingResolvers = null
            }
        },
        rejectPending(reason?: unknown) {
            this.requestConfig = null
            if (this.pendingResolvers) {
                this.pendingResolvers.reject(reason)
                this.pendingResolvers = null
            }
        },
        async retryRequest() {
            if (!this.requestConfig) {
                if (this.pendingResolvers) {
                    this.pendingResolvers.reject(new Error('Missing request to retry'))
                    this.pendingResolvers = null
                }

                return
            }

            const configToRetry = {
                ...this.requestConfig,
            }

            this.requestConfig = null

            const { default: api } = await import('@/api/serviceProviders/my-zone')
            const pendingResolvers = this.pendingResolvers

            this.pendingResolvers = null

            return await api.request(configToRetry)
                .then((response) => {
                    pendingResolvers?.resolve(response)

                    return response
                })
                .catch((error) => {
                    pendingResolvers?.reject(error)

                    throw error
                })
        },
    },
})
